import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { api } from "../lib/api";
import SkylineDivider from "../components/site/SkylineDivider";

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

function renderContent(md) {
  const parts = md.split(/\n\n+/);
  return parts.map((para, idx) => {
    const html = para
      .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
      .replace(/\n/g, "<br/>");
    return <p key={idx} dangerouslySetInnerHTML={{ __html: html }} />;
  });
}

export default function MarketNote() {
  const { slug } = useParams();
  const [note, setNote] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api
      .get(`/market-notes/${slug}`)
      .then((r) => setNote(r.data))
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="bg-cream min-h-[60vh] flex items-center justify-center px-6">
        <div className="text-center">
          <p className="font-display text-3xl text-charcoal">Note not found.</p>
          <Link
            to="/market-notes"
            data-testid="note-back-link"
            className="mt-6 inline-flex items-center gap-2 text-gold link-gold"
          >
            <ArrowLeft size={16} /> Back to Market Notes
          </Link>
        </div>
      </div>
    );
  }

  if (!note) {
    return (
      <div className="bg-cream min-h-[60vh] flex items-center justify-center">
        <div className="font-display italic text-charcoal/50">Loading…</div>
      </div>
    );
  }

  return (
    <article data-testid="market-note-article" className="bg-cream">
      <section className="bg-obsidian text-paper py-24 md:py-32 relative overflow-hidden">
        <div className="skyline"></div>
        <div className="ambient-glow"></div>
        <div className="max-w-[900px] mx-auto px-6 md:px-10 relative z-10">
          <Link
            to="/market-notes"
            data-testid="note-back-link"
            className="inline-flex items-center gap-2 text-warm/70 hover:text-gold text-xs uppercase tracking-[0.24em] mb-10 transition-colors duration-500"
          >
            <ArrowLeft size={14} /> Market Notes
          </Link>
          <div className="flex items-center gap-4 text-[11px] uppercase tracking-[0.24em] text-warm/60">
            <span className="text-gold flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-gold"></span>
              {note.category}
            </span>
            <span className="w-1 h-1 bg-warm/40 rounded-full"></span>
            <span>{formatDate(note.date)}</span>
          </div>
          <h1 className="font-display text-4xl md:text-5xl lg:text-[64px] leading-[1.05] mt-10">
            {note.title}
          </h1>
          <p className="mt-8 text-lg text-warm/80 leading-relaxed font-display italic">
            {note.summary}
          </p>
        </div>
        <SkylineDivider variant="gold" opacity={0.18} height={150} className="skyline-bottom" />
      </section>

      <section className="py-24 md:py-32 relative overflow-hidden">
        <div className="najdi-bg" style={{ opacity: 0.035 }}></div>
        <div className="max-w-[760px] mx-auto px-6 md:px-10 note-content text-[17px] leading-[1.85] relative z-10">
          {/* opening editorial divider */}
          <div className="section-divider mb-12">
            <span className="section-divider-dot"></span>
          </div>

          {renderContent(note.content || "")}

          {note.sources && note.sources.length > 0 && (
            <>
              <div className="section-divider my-14">
                <span className="section-divider-dot"></span>
              </div>
              <h4 className="eyebrow eyebrow-dark mb-4">Sources</h4>
              <ul className="space-y-2">
                {note.sources.map((s, i) => (
                  <li key={i}>
                    <a
                      href={s}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="link-gold text-sm"
                    >
                      {s}
                    </a>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      </section>

      <section className="bg-kingdom text-paper py-24 md:py-32 relative overflow-hidden">
        <div className="arch-depth"></div>
        <div className="max-w-[900px] mx-auto px-6 md:px-10 text-center relative z-10">
          <h2 className="font-display text-3xl md:text-4xl leading-tight">
            Ready to move from signal to <span className="italic text-gold">action?</span>
          </h2>
          <div className="mt-10 flex justify-center">
            <Link
              to="/diagnostic"
              data-testid="note-cta"
              className="btn-gold"
            >
              Find Your First Move
            </Link>
          </div>
        </div>
        <SkylineDivider variant="gold" opacity={0.14} height={140} className="skyline-bottom" />
      </section>
    </article>
  );
}
